import type { QueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { SubfolderType } from "@shared/api";
import { uploadImages, type ImageUploadResult } from "@/lib/image-upload";

function findLocalFileForGalleryPhoto(photo: { file_name?: string }, files: File[]): File | undefined {
  const name = photo.file_name ?? "";
  if (!name) return undefined;
  const exact = files.find((f) => f.name === name);
  if (exact) return exact;
  const strip = (n: string) => n.replace(/\.[^/.]+$/, "");
  const base = strip(name);
  return files.find((f) => {
    const fb = strip(f.name);
    return fb === base || name.includes(fb) || f.name.includes(base);
  });
}

/**
 * Központi galéria feltöltés a közös {@link uploadImages} motorral:
 * fájlonként POST /api/images/photos/upload-to-gallery, timeout + sikertelen → outbox,
 * háttér thumbnail + query invalidálás. Visszaadja a feltöltött / sorba tett darabszámot.
 */
export async function uploadImageFilesToCentralGallery(
  files: File[],
  queryClient: QueryClient
): Promise<ImageUploadResult> {
  if (files.length === 0) {
    return { uploaded: 0, queued: 0, failed: 0, photos: [] };
  }
  const { generateAndUploadThumbnail } = await import("@/lib/image-thumbnail");

  const res = await uploadImages({
    uploadUrl: apiUrl("/api/images/photos/upload-to-gallery"),
    files,
    // no sources => library (EXIF only), capture-time hints always sent
    context: { folderId: "" },
    onEachSuccess: (response) => {
      const data = response as {
        photos?: Array<{ id: string; file_name?: string; subfolder_type?: string }>;
        folderId?: string;
      } | null;
      if (data?.folderId && data.photos?.length) {
        for (const photo of data.photos) {
          const file = findLocalFileForGalleryPhoto(photo, files);
          if (!file || !photo.id) continue;
          const st = photo.subfolder_type;
          const sub: SubfolderType =
            st === "bemeres" || st === "munkaink" || st === "marketing" ? st : "munkaink";
          void generateAndUploadThumbnail(file, data.folderId, sub, photo.id).catch(() => {
            /* nem kritikus */
          });
        }
      }
    },
  });

  await queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
  await queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
  await queryClient.invalidateQueries({ queryKey: ["image-folders"] });
  return res;
}
